/**
 * Activity log display formatter.
 *
 * Transforms raw activity_log descriptions into human-readable text.
 * Does NOT change how data is written — only how it's displayed.
 */

// Status values → friendly display
const STATUS_LABELS: Record<string, string> = {
  new: 'New',
  assigned: 'Assigned',
  en_route: 'En route',
  on_site: 'On site',
  completed: 'Completed',
  cancelled: 'Cancelled',
  pending: 'Pending',
  confirmed: 'Confirmed',
  no_show: 'No-show',
  in_progress: 'In progress',
  on_hold: 'On hold',
  quote_drafting: 'Quote drafting',
  quote_sent: 'Quote sent',
  approved: 'Approved',
  declined: 'Declined',
  deposit_pending: 'Awaiting deposit',
  work_complete: 'Work complete',
  final_invoice_sent: 'Final invoice sent',
  paid: 'Paid',
  closed: 'Closed',
  overdue: 'Overdue',
  draft: 'Draft',
  sent: 'Sent',
  viewed: 'Viewed',
  partial: 'Partially paid',
  void: 'Voided',
  active: 'Active',
  inactive: 'Inactive',
  scheduled: 'Scheduled',
};

function friendlyStatus(raw: string): string {
  return STATUS_LABELS[raw] ?? raw.replace(/_/g, ' ');
}

// Icon suggestions per activity type (lucide icon names)
const ICON_MAP: Record<string, string> = {
  job_created: 'Wrench',
  job_assigned: 'UserCheck',
  job_en_route: 'Navigation',
  job_on_site: 'MapPin',
  job_completed: 'CheckCircle',
  job_cancelled: 'XCircle',
  job_dispatched: 'Send',
  job_reassigned: 'ArrowRightLeft',
  booking_created: 'CalendarCheck',
  booking_completed: 'CheckCircle',
  booking_cancelled: 'XCircle',
  booking_confirmed: 'CalendarCheck',
  booking_pending: 'Clock',
  booking_no_show: 'UserX',
  invoice_created: 'FileText',
  expense_created: 'Receipt',
  payment_recorded: 'DollarSign',
  project_created: 'FolderOpen',
  project_completed: 'CheckCircle',
  project_cancelled: 'XCircle',
  quote_created: 'FileText',
  quote_sent: 'Send',
  quote_approved: 'ThumbsUp',
  quote_declined: 'ThumbsDown',
  schedule_reassigned: 'ArrowRightLeft',
  schedule_moved: 'Calendar',
  schedule_time_changed: 'Clock',
  team_member_added: 'UserPlus',
  vehicle_created: 'Truck',
  vehicle_retired: 'Archive',
  service_created: 'Tag',
  location_created: 'MapPin',
  client_created: 'UserPlus',
  maintenance_scheduled: 'Wrench',
  maintenance_completed: 'CheckCircle',
  settings_updated: 'Settings',
  created: 'Plus',
  updated: 'Pencil',
  cancelled: 'XCircle',
};

export interface FormattedActivity {
  text: string;
  icon: string;
}

/**
 * Format an activity_log entry for human-readable display.
 *
 * Takes the raw `activity_type` and `description` from the DB and
 * returns cleaned-up text. Falls back to the original description
 * if it's already readable.
 */
export function formatActivity(activityType: string, description: string): FormattedActivity {
  const icon = ICON_MAP[activityType] ?? 'Activity';
  let text = description;

  // Pattern: "Job status → completed" → "Job completed"
  const statusArrow = text.match(/^(.+?)\s*(?:status\s*)?→\s*(.+)$/);
  if (statusArrow) {
    const [, prefix, status] = statusArrow;
    const cleanPrefix = prefix.replace(/\s*status$/, '').trim();
    text = `${cleanPrefix} ${friendlyStatus(status)}`;
  }

  // Pattern: "Entry reassigned via drag-drop" → "Schedule entry reassigned"
  if (text === 'Entry reassigned via drag-drop') {
    text = 'Schedule entry reassigned';
  }

  // Pattern: "Entry moved to 2025-03-15" → "Schedule entry moved to Mar 15"
  const movedMatch = text.match(/^Entry moved to (\d{4}-\d{2}-\d{2})$/);
  if (movedMatch) {
    const d = new Date(movedMatch[1] + 'T12:00:00');
    const friendly = d.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
    text = `Schedule entry moved to ${friendly}`;
  }

  // Pattern: "Entry time shifted by 30min" → "Schedule entry shifted 30min"
  const shiftMatch = text.match(/^Entry time shifted by (.+)$/);
  if (shiftMatch) {
    text = `Schedule entry shifted ${shiftMatch[1]}`;
  }

  // Pattern: "Booking pending" → "Booking marked pending"
  if (/^Booking (confirmed|pending|completed|cancelled|no_show)$/.test(text)) {
    const status = text.split(' ')[1];
    text = `Booking ${friendlyStatus(status).toLowerCase()}`;
  }

  // Pattern: "Job dispatched to crew" → keep as-is (already readable)

  // Pattern: "Leave request approved/rejected/cancelled" → proper case
  const leaveMatch = text.match(/^Leave request (approved|rejected|cancelled)$/);
  if (leaveMatch) {
    text = `Leave request ${leaveMatch[1]}`;
  }

  // Pattern: raw underscored status anywhere — clean up
  text = text.replace(/\b(on_hold|no_show|en_route|on_site|quote_drafting|quote_sent|deposit_pending|work_complete|final_invoice_sent)\b/g, (m) => friendlyStatus(m));

  // Pattern: "Business settings updated: timezone, name, ..." → keep as-is

  return { text, icon };
}
